import { FormEvent, useState, useEffect } from 'react';
import { CellarMember, CellarInvitation, UserProfile } from '../types';
import { supabase } from '../lib/supabase';

interface MembersPanelProps {
  members: CellarMember[];
  pendingInvites: CellarInvitation[];
  currentUserId: string;
  ownerId: string;
  onInvite: (email: string) => Promise<void>;
  onClose: () => void;
}

export function MembersPanel({ members, pendingInvites, currentUserId, ownerId, onInvite, onClose }: MembersPanelProps) {
  const [inviteEmail, setInviteEmail] = useState('');
  const [profiles, setProfiles] = useState<Record<string, UserProfile>>({});
  const [inviting, setInviting] = useState(false);

  useEffect(() => {
    loadMemberInfo();
  }, [members]);

  async function loadMemberInfo() {
    const userIds = members.map(m => m.user_id);
    
    const { data: profileData } = await supabase
      .from('user_profiles')
      .select('*')
      .in('user_id', userIds);

    if (profileData) {
      const profileMap: Record<string, UserProfile> = {};
      profileData.forEach(p => { profileMap[p.user_id] = p; });
      setProfiles(profileMap);
    }
  }

  function getMemberDisplay(member: CellarMember): string {
    if (member.user_id === currentUserId) return 'You';
    const profile = profiles[member.user_id];
    if (profile?.username) return `@${profile.username}`;
    return 'Member';
  }

  function getMemberInitial(member: CellarMember): string {
    if (member.user_id === currentUserId) return 'Y';
    const profile = profiles[member.user_id];
    if (profile?.username) return profile.username[0].toUpperCase();
    return 'M';
  }

  const handleSubmitInvite = async (e: FormEvent) => {
    e.preventDefault();
    setInviting(true);
    try {
      await onInvite(inviteEmail);
      setInviteEmail('');
    } finally {
      setInviting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div 
        className="ios-sheet w-full"
        onClick={e => e.stopPropagation()}
      >
        <div className="ios-sheet-handle" />

        {/* Header */}
        <div style={{ 
          padding: '0 20px 16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{ width: '50px' }} />
          <h2 className="wine-title" style={{ 
            fontSize: '18px', 
            margin: 0,
            color: 'var(--color-text)'
          }}>
            Cellar Members
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--color-wine)',
              fontSize: '16px',
              cursor: 'pointer'
            }}
          >
            Done
          </button>
        </div>

        <div style={{ padding: '0 20px 20px' }}>
          {/* Invite Section */}
          {currentUserId === ownerId && (
            <div style={{ marginBottom: '24px' }}>
              <label className="ios-label">Invite by Email</label>
              <form onSubmit={handleSubmitInvite} style={{ display: 'flex', gap: '8px' }}>
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="friend@email.com"
                  className="ios-input"
                  style={{ flex: 1 }}
                  autoComplete="email"
                  required
                />
                <button
                  type="submit"
                  disabled={inviting}
                  className="ios-btn ios-btn-primary"
                  style={{ flexShrink: 0 }}
                >
                  {inviting ? '...' : 'Invite'}
                </button>
              </form>
            </div>
          )}

          {/* Pending Invitations */}
          {pendingInvites.length > 0 && (
            <div style={{ marginBottom: '20px' }}>
              <label className="ios-label">Pending Invitations</label>
              <div className="ios-card" style={{ overflow: 'hidden' }}>
                {pendingInvites.map((invite, index) => (
                  <div 
                    key={invite.id} 
                    className="ios-list-row"
                    style={{ 
                      borderBottom: index < pendingInvites.length - 1 ? '1px solid var(--color-border)' : 'none'
                    }}
                  >
                    <div 
                      style={{ 
                        width: '36px',
                        height: '36px',
                        borderRadius: '18px',
                        background: 'var(--color-parchment-dark)',
                        color: 'var(--color-text-muted)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '16px'
                      }}
                    >
                      ✉️
                    </div>
                    <div style={{ flex: 1 }}>
                      <p style={{ fontSize: '15px', color: 'var(--color-text)' }}>
                        {invite.invitee_email}
                      </p>
                      <p style={{ fontSize: '12px', color: 'var(--color-text-muted)' }}>
                        Invited {new Date(invite.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <span className="badge badge-gold">Pending</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Current Members */}
          <div>
            <label className="ios-label">Current Members</label>
            <div className="ios-card" style={{ overflow: 'hidden' }}>
              {members.map((member, index) => (
                <div 
                  key={index} 
                  className="ios-list-row"
                  style={{ 
                    borderBottom: index < members.length - 1 ? '1px solid var(--color-border)' : 'none'
                  }}
                >
                  <div 
                    style={{ 
                      width: '36px',
                      height: '36px',
                      borderRadius: '18px',
                      background: member.role === 'owner' ? 'var(--color-wine)' : 'var(--color-gold)',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '14px',
                      fontWeight: '600'
                    }}
                  >
                    {getMemberInitial(member)}
                  </div>
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: '16px', color: 'var(--color-text)' }}>
                      {getMemberDisplay(member)}
                    </p>
                    {member.role === 'owner' && (
                      <p style={{ fontSize: '13px', color: 'var(--color-wine)' }}>Owner</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
